import React, { useState, useEffect } from 'react';
import { FaTimes, FaEnvelope, FaLock } from 'react-icons/fa';
import '../pages/Home.css';
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const THEME = '#636CCB';

export default function LoginModal({ onClose, onLoginSuccess, openSignup }) {
  const navigate = useNavigate();

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const syncTheme = () => {
      const savedMode = localStorage.getItem("darkMode");
      setDarkMode(savedMode ? JSON.parse(savedMode) : false);
    };
    window.addEventListener("themeChanged", syncTheme);
    return () => window.removeEventListener("themeChanged", syncTheme);
  }, []);

  const styles = {
    overlay: {
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 10000
    },
    card: {
      width: 420, maxWidth: '92%', borderRadius: 12,
      background: darkMode ? '#1e1e1e' : '#fff',
      padding: 28, boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
      position: 'relative', fontFamily: 'Inter, Arial, sans-serif',
      color: darkMode ? '#eee' : '#333'
    },
    title: { fontSize: 22, fontWeight: 700, marginBottom: 6, textAlign: 'center', color: darkMode ? '#fff' : '#000' },
    subtitle: { color: darkMode ? '#aaa' : '#666', textAlign: 'center', marginBottom: 18 },
    fieldWrap: { position: 'relative', marginBottom: 14 },
    iconStyle: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: darkMode ? '#aaa' : '#666' },
    inputStyle: {
      width: '85%',
      padding: '12px 14px 12px 42px',
      borderRadius: 8,
      border: `1px solid ${darkMode ? '#444' : '#e6e6e6'}`,
      outline: 'none',
      fontSize: 15,
      background: darkMode ? '#2d2d2d' : '#fff',
      color: darkMode ? '#eee' : '#333'
    },
    buttonPrimary: {
      width: '100%',
      background: THEME,
      color: 'white',
      border: 'none',
      padding: '12px',
      borderRadius: 8,
      fontWeight: 700,
      cursor: 'pointer',
      fontSize: 16
    },
    linkStyle: { color: THEME, textDecoration: 'none', fontWeight: 600, cursor: 'pointer' },
    smallGray: { fontSize: 13, color: darkMode ? '#aaa' : '#666' }
  };

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [step, setStep] = useState('login');
  const [otpSentTo, setOtpSentTo] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newPassConfirm, setNewPassConfirm] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [oldPassword, setOldPassword] = useState("");
  const [loginOtpTimer, setLoginOtpTimer] = useState(0);

  async function checkIfAdmin(email) {
    try {
      const res = await fetch(`${API_BASE}/admins/check/${email}`);
      const data = await res.json();
      return data.isAdmin === true;
    } catch (err) {
      console.error("Admin check failed:", err);
      return false;
    }
  }


  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.disableAutoSelect();

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          use_fedcm_for_prompt: false,
          prompt_parent_id: "googleBtn"
        });

        window.google.accounts.id.renderButton(
          document.getElementById("googleBtn"),
          { theme: "outline", size: "large", width: "350" }
        );
      }
    };
  }, []);

  async function handleGoogleResponse(response) {
    try {
      const decoded = jwtDecode(response.credential);

      const googleEmail = decoded.email;
      const googleName = decoded.name;
      const googlePicture = decoded.picture;

      fetch(`${API_BASE}/google-auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: googleEmail,
          name: googleName,
        }),
      })
        .then(async (r) => {
          if (!r.ok) {
            const err = await r.json();
            setErrorMsg(err.error || "Google login failed");

            localStorage.clear();
            return null;
          }

          return r.json();
        })
        .then(async (data) => {
          if (!data) return;

          // ✔ Save user info
          localStorage.setItem("userEmail", googleEmail);
          localStorage.setItem("googleEmail", googleEmail);
          localStorage.setItem("userName", data.username || googleName);
          localStorage.setItem("googleName", googleName);
          localStorage.setItem("googlePicture", googlePicture || "");

          // Save user_id from backend (UUID v4)
          if (data.user_id) {
            localStorage.setItem("user_id", data.user_id);
          }

          // Save JWT token
          if (data.token) {
            localStorage.setItem("token", data.token);
          }

          // CHECK IF GOOGLE USER IS ADMIN
          const isAdmin = await checkIfAdmin(googleEmail);

          window.dispatchEvent(new Event("userLoggedIn"));
          onClose();

          if (isAdmin) {
            localStorage.setItem("adminEmail", googleEmail);
            navigate("/admin");       // redirect to admin dashboard
          } else {
            localStorage.removeItem("adminEmail");
            navigate("/DetectorApp"); // normal user
          }
        })
        .catch((err) => {
          console.error("Google auth error:", err);
          setErrorMsg("Google login failed");
        });

    } catch (err) {
      console.error("Google Login Error:", err);
    }
  }

  useEffect(() => {
    setEmail("");
    setPassword("");
    setErrorMsg("");
    setEmailError("");
    setOtp("");
    setOtpError("");
    setNewPass("");
    setNewPassConfirm("");
    setResetMsg("");
    setStep("login");
    setLoginOtpTimer(0);
  }, []);

  useEffect(() => {
    if (otpTimer <= 0) return;
    const interval = setInterval(() => {
      setOtpTimer((t) => t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  useEffect(() => {
    if (loginOtpTimer <= 0) return;
    const interval = setInterval(() => {
      setLoginOtpTimer((t) => t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [loginOtpTimer]);


  function validateEmailFormat(value) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(value || '').toLowerCase());
  }

  function validateStrongPassword(pass) {
    const strongPassRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*_?&])[A-Za-z\d@$!%*_?&]{8,}$/;
    return strongPassRegex.test(pass);
  }

  const handleLogin = async (e) => {
    e?.preventDefault();
    setErrorMsg('');

    if (!email) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmailFormat(email)) {
      setEmailError('Invalid email');
      return;
    }
    setEmailError('');

    if (!password) {
      setErrorMsg('Password is required');
      return;
    }

    // All logins (including admin) go through the backend
    try {
      setIsSending(true);
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.error || "Login failed");
        setIsSending(false);
        return;
      }

      // Save token + user info
      if (data.token) localStorage.setItem("token", data.token);
      if (data.user_id) localStorage.setItem("user_id", data.user_id);

      const isAdmin = data.isAdmin || (await checkIfAdmin(email));

      if (isAdmin) {
        localStorage.setItem("adminEmail", email);
        localStorage.removeItem("userEmail");
        localStorage.removeItem("googleEmail");
        window.dispatchEvent(new Event("userLoggedIn"));
        onLoginSuccess("Admin");
        onClose();
        navigate("/admin");
      } else {
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userName", data.username);
        localStorage.removeItem("adminEmail");
        window.dispatchEvent(new Event("userLoggedIn"));
        onClose();
        navigate("/DetectorApp");
      }

    } catch (err) {
      setErrorMsg("Server error. Try again.");
      console.error(err);
    } finally {
      setIsSending(false);
    }
  };

  const requestOtp = async () => {
    setEmailError('');
    setResetMsg('');

    if (!email) return setEmailError("Email is required");
    if (!validateEmailFormat(email)) return setEmailError("Invalid email");


    setIsSending(true);
    try {
      const res = await fetch(`${API_BASE}/request-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      if (!res.ok) {
        setResetMsg(data.error || "Failed to send code");
        setIsSending(false);
        return;
      }

      setOtpSentTo(email);
      setStep("verify");
      setOtpTimer(60);

    } catch (err) {
      setResetMsg("Server error. Try again.");
      console.error(err);
    } finally {
      setIsSending(false);
    }
  };

  const verifyOtp = async () => {
    setOtpError('');
    if (!otp) return setOtpError("Enter the code sent to your email");

    setIsVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: otpSentTo, otp }),
      });

      const data = await res.json();
      if (!res.ok) {
        setOtpError(data.error || "Invalid code");
        setIsVerifying(false);
        return;
      }
      setOldPassword(data.oldPassword);
      setStep("reset");

    } catch (err) {
      setOtpError("Server error. Try again.");
      console.error(err);
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyLoginOtp = async () => {
    setOtpError('');
    if (!otp) return setOtpError("Enter the 6-digit code sent to your email");

    setIsVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/verify-login-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: otpSentTo, otp }),
      });

      const data = await res.json();
      if (!res.ok) {
        setOtpError(data.error || "Invalid code");
        setIsVerifying(false);
        return;
      }

      // Login successful
      const isAdmin = await checkIfAdmin(otpSentTo);

      localStorage.setItem("userEmail", otpSentTo);
      localStorage.setItem("userName", data.username);

      // Save user_id from backend (UUID v4)
      if (data.user_id) {
        localStorage.setItem("user_id", data.user_id);
      }

      // Save JWT token
      if (data.token) {
        localStorage.setItem("token", data.token);
      }

      window.dispatchEvent(new Event("userLoggedIn"));
      onClose();

      if (isAdmin) {
        localStorage.setItem("adminEmail", otpSentTo);
        navigate("/admin");
      } else {
        localStorage.removeItem("adminEmail");
        navigate("/DetectorApp");
      }

    } catch (err) {
      setOtpError("Server error. Try again.");
      console.error(err);
    } finally {
      setIsVerifying(false);
    }
  };

  const doReset = async () => {
    setResetMsg("");

    if (!newPass || !newPassConfirm)
      return setResetMsg("Please fill both password fields");

    if (!validateStrongPassword(newPass))
      return setResetMsg("Password must have uppercase, lowercase, numbers, symbols, and be at least 8 characters");

    try {
      const checkRes = await fetch(`${API_BASE}/check-old-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: otpSentTo, newPassword: newPass }),
      });

      const checkData = await checkRes.json();
      if (!checkRes.ok) {
        return setResetMsg(checkData.error || "Route not found");
      }

      if (checkData.same) {
        return setResetMsg("New password cannot be the same as your previous password.");
      }
    } catch (error) {
      return setResetMsg("Server error checking password.");
    }

    if (newPass !== newPassConfirm)
      return setResetMsg("Passwords do not match");

    setIsResetting(true);
    try {
      const res = await fetch(`${API_BASE}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: otpSentTo, otp, newPassword: newPass }),
      });

      const data = await res.json();

      if (!res.ok) {
        setIsResetting(false);
        return setResetMsg(data.error || "Failed to reset password");
      }

      setResetMsg("");
      setStep("login");
      setEmail(otpSentTo);
      setPassword("");

    } catch (err) {
      setResetMsg("Server error. Try again.");
    } finally {
      setIsResetting(false);
    }
  };

  const renderLogin = () => (
    <>
      <h3 style={styles.title}>Welcome!</h3>
      <div style={styles.subtitle}>Sign in to continue using SourceCheck</div>

      {errorMsg && <div style={{ color: "red", textAlign: "center", marginBottom: 10 }}>{errorMsg}</div>}

      <form onSubmit={handleLogin} autoComplete="off">
        <div style={styles.fieldWrap}>
          <FaEnvelope style={styles.iconStyle} />
          <input
            style={styles.inputStyle}
            type="email"
            autoComplete="off"
            placeholder="Email address"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(""); setErrorMsg(""); }}
          />
          {emailError && <div style={{ color: "red", textAlign: "center", marginTop: 6 }}>{emailError}</div>}
        </div>

        <div style={styles.fieldWrap}>
          <FaLock style={styles.iconStyle} />
          <input
            style={styles.inputStyle}
            type="password"
            autoComplete="new-password"
            placeholder="Password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setErrorMsg(""); }}
          />
        </div>

        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <span style={styles.linkStyle} onClick={() => setStep("request")}>
            Forgot password?
          </span>
        </div>

        <button style={styles.buttonPrimary} type="submit">Sign In</button>
      </form>

      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <div style={styles.smallGray}>
          Don't have an account?{" "}
          <span
            style={styles.linkStyle}
            onClick={() => {
              onClose();
              openSignup();
            }}
          >
            Signup
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18 }}>
        <div style={{ flex: 1, height: 1, background: darkMode ? '#444' : '#eee' }} />
        <div style={{ color: darkMode ? '#aaa' : '#666', fontSize: 13 }}>OR</div>
        <div style={{ flex: 1, height: 1, background: darkMode ? '#444' : '#eee' }} />
      </div>

      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
        <div id="googleBtn"></div>
      </div>
    </>
  );

  const renderRequest = () => (
    <>
      <h3 style={styles.title}>Reset your password</h3>
      <div style={styles.subtitle}>Enter your account email and we’ll send a verification code.</div>

      {resetMsg && <div style={{ color: "red", textAlign: "center", marginBottom: 8 }}>{resetMsg}</div>}

      <div style={styles.fieldWrap}>
        <FaEnvelope style={styles.iconStyle} />
        <input
          style={styles.inputStyle}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setEmailError(""); setResetMsg(""); }}
        />
        {emailError && <div style={{ color: 'red', textAlign: "center", marginTop: 6 }}>{emailError}</div>}
      </div>

      <button style={styles.buttonPrimary} onClick={requestOtp} disabled={isSending}>
        {isSending ? "Sending..." : "Send verification code"}
      </button>

      <div style={{ textAlign: 'center', marginTop: 14 }}>
        <span style={styles.linkStyle} onClick={() => setStep("login")}>Back to sign in</span>
      </div>
    </>
  );

  const renderVerify = () => (
    <>
      <h3 style={styles.title}>Enter verification code</h3>
      <div style={styles.subtitle}>We sent a 6-digit code to <strong>{otpSentTo}</strong></div>

      {otpError && <div style={{ color: 'red', textAlign: "center", marginBottom: 8 }}>{otpError}</div>}

      <div style={styles.fieldWrap}>
        <input
          style={{ ...styles.inputStyle, paddingLeft: 14 }}
          type="text"
          placeholder="Enter code"
          value={otp}
          onChange={(e) => {
            setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
            setOtpError("");
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button style={{ ...styles.buttonPrimary, flex: 1 }} onClick={verifyOtp} disabled={isVerifying}>
          {isVerifying ? "Verifying..." : "Verify code"}
        </button>

        <button
          style={{
            flex: 1,
            border: `1px solid ${darkMode ? '#444' : '#e6e6e6'}`,
            borderRadius: 8,
            background: otpTimer > 0 ? (darkMode ? '#333' : '#f3f3f3') : (darkMode ? '#2d2d2d' : '#fff'),
            color: otpTimer > 0 ? '#999' : (darkMode ? '#eee' : '#333'),
            cursor: otpTimer > 0 ? 'not-allowed' : 'pointer'
          }}
          disabled={otpTimer > 0}
          onClick={() => {
            if (otpTimer === 0) {
              setStep("request");
              setOtp("");
            }
          }}
        >
          {otpTimer > 0 ? `Resend in ${otpTimer}s` : "Resend"}
        </button>

      </div>

      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <span style={styles.linkStyle} onClick={() => setStep("login")}>Back to sign in</span>
      </div>
    </>
  );

  const renderReset = () => (
    <>
      <h3 style={styles.title}>Create a new password</h3>
      <div style={styles.subtitle}>Choose a strong password you haven't used elsewhere.</div>

      {resetMsg && <div style={{ color: 'red', textAlign: 'center', marginBottom: 8 }}>{resetMsg}</div>}

      <div style={styles.fieldWrap}>
        <FaLock style={styles.iconStyle} />
        <input
          style={styles.inputStyle}
          type="password"
          placeholder="New password"
          value={newPass}
          onChange={(e) => { setNewPass(e.target.value); setResetMsg(""); }}
        />
      </div>

      <div style={styles.fieldWrap}>
        <FaLock style={styles.iconStyle} />
        <input
          style={styles.inputStyle}
          type="password"
          placeholder="Confirm new password"
          value={newPassConfirm}
          onChange={(e) => { setNewPassConfirm(e.target.value); setResetMsg(""); }}
        />
      </div>

      <button style={styles.buttonPrimary} onClick={doReset} disabled={isResetting}>
        {isResetting ? "Updating..." : "Update password"}
      </button>

      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <span style={styles.linkStyle} onClick={() => setStep("login")}>Back to sign in</span>
      </div>
    </>
  );

  const renderVerifyLogin = () => (
    <>
      <h3 style={styles.title}>Verify your email</h3>
      <div style={styles.subtitle}>We sent a 6-digit code to <strong>{otpSentTo}</strong></div>

      {otpError && <div style={{ color: 'red', textAlign: "center", marginBottom: 8 }}>{otpError}</div>}

      <div style={styles.fieldWrap}>
        <input
          style={{ ...styles.inputStyle, paddingLeft: 14, textAlign: 'center', letterSpacing: '2px', fontSize: '18px' }}
          type="text"
          placeholder="000000"
          value={otp}
          onChange={(e) => {
            setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
            setOtpError("");
          }}
          maxLength="6"
          disabled={isVerifying}
        />
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button style={{ ...styles.buttonPrimary, flex: 1 }} onClick={verifyLoginOtp} disabled={isVerifying}>
          {isVerifying ? "Verifying..." : "Verify code"}
        </button>

        <button
          style={{
            flex: 1,
            border: `1px solid ${darkMode ? '#444' : '#e6e6e6'}`,
            borderRadius: 8,
            background: loginOtpTimer > 0 ? (darkMode ? '#333' : '#f3f3f3') : (darkMode ? '#2d2d2d' : '#fff'),
            color: loginOtpTimer > 0 ? '#999' : (darkMode ? '#eee' : '#333'),
            cursor: loginOtpTimer > 0 ? 'not-allowed' : 'pointer',
            fontWeight: 600
          }}
          disabled={loginOtpTimer > 0}
          onClick={() => {
            if (loginOtpTimer === 0) {
              // Resend OTP
              handleLogin({ preventDefault: () => { } });
            }
          }}
        >
          {loginOtpTimer > 0 ? `Resend in ${Math.floor(loginOtpTimer / 60)}:${String(loginOtpTimer % 60).padStart(2, '0')}` : "Resend"}
        </button>
      </div>

      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <span style={styles.linkStyle} onClick={() => setStep("login")}>Back to sign in</span>
      </div>
    </>
  );

  return (
    <div style={styles.overlay}>
      <div style={styles.card} autoComplete="off">
        <button
          style={{ position: 'absolute', top: 12, right: 12, border: 'none', background: 'transparent', cursor: 'pointer', color: darkMode ? '#fff' : '#000' }}
          onClick={onClose}
        >
          <FaTimes />
        </button>

        {step === 'login' && renderLogin()}
        {step === 'request' && renderRequest()}
        {step === 'verify' && renderVerify()}
        {step === 'verify-login' && renderVerifyLogin()}
        {step === 'reset' && renderReset()}
      </div>
    </div>
  );
}